import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import * as firebase from 'firebase';
import * as csvGenerator from 'csv-generator-client';
import * as moment from 'moment';
import * as _ from 'lodash';
import { extendMoment } from 'moment-range';
import InfiniteCalendar, { Calendar, withRange } from 'react-infinite-calendar';
import 'react-infinite-calendar/styles.css';

import { deepEqual } from '../utils';
import { toCleanedASCII } from '../helpers/string';
import { INFINITE_CALENDAR_DEFAULTS } from '../constants';

const rangeMoment = extendMoment(moment);

class WorkedDaysQuadrant extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isFetching: false,
      isFetched: false,
      period: {
        start: new Date(),
        end: new Date(),
      },
      workedDays: {},
    };

    this.onPeriodChange = this.onPeriodChange.bind(this);
  }

  shouldComponentUpdate(nextProps, nextState) {
    return !deepEqual(this.state, nextState);
  }

  onPeriodChange(props) {
    this.setState({
      period: {
        start: props.start,
        end: props.end,
      },
    });

    if (props.eventType === 3) { this.fetchReportData(); }
  }

  isValid() {
    const { start, end } = this.state.period;
    return start && end;
  }

  async fetchReportData() {
    if (!this.isValid()) { return; }
    this.setState({ isFetching: true });

    const { start, end } = this.state.period;
    const daysOfPeriod = moment(end).diff(start, 'days') + 1;

    const workedDaysData = await Promise.all(this.props.driverIds.map(id =>
      this.fetchDriverWorkedDaysData(id, daysOfPeriod)));

    const workedDays = {};
    this.props.driverIds.forEach((id, index) => {
      workedDays[id] = workedDaysData[index] || {};
    });

    this.setState({
      isFetching: false,
      isFetched: true,
      workedDays,
    });
  }

  async fetchDriverWorkedDaysData(driverId, limitTo) {
    return (await firebase.database()
      .ref(`/clients/${this.props.client}/driver/workedDays/${driverId}`)
      .orderByKey()
      .startAt(moment(this.state.period.start).format('YYYYMMDD'))
      .limitToFirst(limitTo)
      .once('value')).val();
  }

  sumWorkedDays(workedDays, periodDays) {
    let sum = 0;
    for (const day of periodDays) {
      const dayId = day.format('YYYYMMDD');
      sum += (workedDays[dayId] ? 1 : 0);
    }
    return sum;
  }

  downloadCSV(drivers, days) {
    const settings = {
      separator: ',',
      addQuotes: true,
      autoDetectColumns: false,
    };
    const fileName = `worked-days-${_.first(days).format('YYYYMMDD')}-${_.last(days).format('YYYYMMDD')}.csv`;
    const columnKeys = ['Nome', 'Frota', 'Total'];
    for (const day of days) columnKeys.push(day.format('DD/MM/YYYY'));
    const dataArray = [columnKeys];
    for (const driver of drivers) {
      const driverRow = [
        toCleanedASCII(`${driver.firstName} ${driver.surname}`),
        toCleanedASCII(driver.fleet && driver.fleet !== '' ? this.props.fleets[driver.fleet].name : ''),
        this.sumWorkedDays(driver.workedDays, days),
      ];
      for (const day of days) {
        const dayId = day.format('YYYYMMDD');
        driverRow.push(driver.workedDays[dayId] ? 1 : 0);
      }
      dataArray.push(driverRow);
    }
    csvGenerator.download({ settings, fileName, dataArray });
  }

  renderDriverRow(driver, days) {
    const totalWorkedDays = this.sumWorkedDays(driver.workedDays, days);
    return (
      <tr key={driver.$key}>
        <td style={{ whiteSpace: 'nowrap' }}>{driver.firstName} {driver.surname}</td>
        <td style={{ whiteSpace: 'nowrap' }}>
          {driver.fleet && driver.fleet !== '' ? this.props.fleets[driver.fleet].name : ''}
        </td>
        <td className="align-center">{totalWorkedDays}</td>
        {days.map((day) => {
          const dayId = day.format('YYYYMMDD');
          return (<td key={dayId} className="align-center">{driver.workedDays[dayId] ? 1 : 0}</td>);
        })}
      </tr>
    );
  }

  render() {
    const { start, end } = this.state.period;

    if (this.state.isFetching) {
      return (
        <span>Carregando...</span>
      );
    }

    if (!this.state.isFetched) {
      const today = moment().toDate();
      return (
        <InfiniteCalendar
          Component={withRange(Calendar)}
          width={760}
          max={today}
          maxDate={today}
          selected={this.state.period}
          onSelect={this.onPeriodChange}
          locale={INFINITE_CALENDAR_DEFAULTS}
        />
      );
    }

    const range = rangeMoment.range(moment(start), moment(end));
    const days = Array.from(range.by('days'));

    const years = _.chain(days)
      .groupBy(day => day.format('YYYY'))
      .value();

    const driversChain = _.chain(this.props.driverIds)
      .map(id => ({
        $key: id,
        ...this.props.drivers[id],
        fleetName: this.props.drivers[id].fleet && this.props.drivers[id].fleet !== '' ? this.props.fleets[this.props.drivers[id].fleet].name : '',
        workedDays: this.state.workedDays[id],
      }))
      .sortBy(['fleetName', 'firstName', 'surname']);

    return (
      <table className="mm-popup--events-table">
        <thead>
          <tr>
            <td colSpan={3}>
              <a href="#download_csv" onClick={() => this.downloadCSV(driversChain.value(), days)}>Exportar Excel</a>
            </td>
          </tr>
          <tr>
            <th rowSpan={2}>Motorista</th>
            <th rowSpan={2}>Frota</th>
            <th rowSpan={2} className="align-center">Total</th>
            {_.map(years, (yearDays, year) => (
              <th key={year} colSpan={yearDays.length} className="align-center">{year}</th>
            ))}
          </tr>
          <tr>
            {days.map(day => (
              <th key={day.format('YYYYMMDD')} className="align-center">
                {day.format('DD')}
                <br />
                {day.format('ddd')}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {driversChain.map(driver => this.renderDriverRow(driver, days)).value()}
        </tbody>
      </table>
    );
  }
}

WorkedDaysQuadrant.propTypes = {
  driverIds: PropTypes.array.isRequired,
  client: PropTypes.string.isRequired,
  drivers: PropTypes.object.isRequired,
  fleets: PropTypes.object.isRequired,
};

const mapStateToProps = store => ({
  client: store.authState.currentUser.client,
  drivers: store.driverState.drivers,
  fleets: store.fleetState.fleets,
});

export default connect(mapStateToProps, null)(WorkedDaysQuadrant);



// WEBPACK FOOTER //
// ./src/Driver/WorkedDaysQuadrant.js